import type { PostTestInstanceResponseMarkItem } from 'services/assist-api/entities/test-instance-response-mark-item/types';
import type { TestInstanceResponseInputType } from 'services/assist-api/entities/test-instance-response/types';

export enum MarkType {
  Autoscored = 'autoscored',
  Manual = 'manual'
}

export enum MarkStatus {
  Assigned = 'assigned',
  InProgress = 'inProgress',
  NeedsReview = 'needsReview',
  InReview = 'inReview',
  FurtherEscalated = 'furtherEscalated',
  ResponseReopened = 'responseReopened',
  ReturnedForMarking = 'returnedForMarking',
  Reassigned = 'reassigned',
  FinishedSuccess = 'finishedSuccess',
  FinishedFailure = 'finishedFailure',
  Replaced = 'replaced'
}

type SmallestTestInstanceResponseMark = {
  status: MarkStatus;
  needsAttention: boolean;
  comment: string | null;
};

export type BaseTestInstanceResponseMark = API_JSON & {
  type: MarkType;
} & SmallestTestInstanceResponseMark;

// Types for displaying a particular mark:
export type Marker = API_JSON & { user: API_JSON };
export type MarkItem = {
  id: number;
  submittedOptions: API_JSON[];
  submittedValue: string;
  submittedScore: number;
  inputType: TestInstanceResponseInputType;
  testInstanceResponseMarker?: Marker;
  testRubricItem: API_JSON;
  testInstanceResponseItem: API_JSON;
};
export type TestInstanceResponseMarkWithItems = BaseTestInstanceResponseMark & {
  activeMarker?: Marker;
  activeReviewer?: API_JSON;
  markItems?: MarkItem[];
  markers: API_JSON[];
  tags: API_JSON[];
  replacementTestInstanceResponseMark?: API_JSON;
};

// Types for saving a mark:
export enum MarkReplacementReason {
  AutoscoringAccepted = 'autoscoringAccepted',
  AutoscoringRejected = 'autoscoringRejected',
  AutoscoringModified = 'autoscoringModified'
}
export type PostAndPatchMarkFields = SmallestTestInstanceResponseMark & {
  activeMarkerId: number;
  testInstanceResponseId: number;
  testRubricId: number;
  tagIds?: number[];
  activeReviewerId?: number | null;
  replacedTestInstanceResponseMarkId?: number;
  replacedTestInstanceResponseMarkReplacementReason?: MarkReplacementReason;
};

export type PostTestInstanceResponseMark = PostAndPatchMarkFields & {
  testInstanceResponseMarkItems?: PostTestInstanceResponseMarkItem[];
};
export type PatchTestInstanceResponseMark = {
  id: number;
  data: Partial<PostAndPatchMarkFields>;
};

// Types for data specific to report hooks
export type ReportTestInstanceResponseMark = BaseTestInstanceResponseMark & {
  roundedScore: number;
  roundedMaxScore: number;
  scaleScore: number;
  scaleError: string;
  holisticComment: string | null;
  activeMarker?: Marker;
  markers?: Marker[];
  markItems?: ReportMarkItem[];
  testInstanceResponse: API_JSON;
};

export type ReportMarkItem = API_JSON & {
  comment?: string;
  submittedOptions?: API_JSON[];
  roundedSubmittedScore?: number;
  testRubricItem: API_JSON;
};

// Types for data specific to pages that are used to manage marking:
export type ManageMarkingMark = BaseTestInstanceResponseMark & {
  testRubric: API_JSON;
  testInstanceResponse: API_JSON;
  replacementTestInstanceResponseMark?: API_JSON;
  activeReviewer?: API_JSON;
  activeMarker?: Marker;
  markers?: Marker[];
  markItems?: ManageMarkingMarkItem[];
};

export type ManageMarkingMarkItem = API_JSON & {
  testInstanceResponseMarker: API_JSON;
  testInstanceResponseItem: API_JSON;
  testRubricItem: API_JSON;
  inputType: TestInstanceResponseInputType;
  submittedValue: string;
};

// Types containing data that can be viewed by the Teacher role:
export type TeacherAuthorisedMark = ReportTestInstanceResponseMark | ManageMarkingMark;
